import React, { useMemo } from 'react';
import { GlobalFilterState } from './GlobalFilterState';
import { MultiSelect } from '@progress/kendo-react-dropdowns';
import { GEAR_FIELD_TYPE } from '../../../../global/enums/GearFieldType';
import { useSearchContext } from '../../../../context/search/SearchContext';
import { SelectionRange } from '@progress/kendo-react-dateinputs';
import { CompositeFilterDescriptor, FilterDescriptor, FilterOperator } from '@progress/kendo-data-query';
import { GearFilterRequest } from '../../../../Model/GearFilterRequest';
import { useDocumentsContext } from '../../../../context/documents/DocumentsContext';
import { ControlledInput } from './ControlledInput';
import { GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT } from '../../../../Model/enums/GearUserAllowedActions';
import { PERMISSIONS_FIELD } from '../../../../config/consts';
import { useGlobalizationContext } from '../../../../context/globalization/GlobalizationContext';
import { useTranslation } from '../../../../context/globalization/useTranslation';

interface GlobalFilterProps {
  filterState: GlobalFilterState;
}

type GlobalFilterValueUnionType = string | number | string[];

type HandleChangeValueUnionType = string[] | boolean[] | number[];

const getArrayValue = (value: GlobalFilterValueUnionType | undefined): string[] | undefined => {
  if (!value) {
    return undefined;
  }
  return Array.isArray(value) ? value : [value.toString()];
};

export const GlobalFilter: React.FC<GlobalFilterProps> = props => {
  const { filterState } = props;
  const { queryRequest, setQueryRequest, compositeFilterDescriptor, setCompositeFilterDescriptor } = useSearchContext();
  const { setSelectedDocuments } = useDocumentsContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const currentValue = useMemo(() => {
    if (filterState.name === PERMISSIONS_FIELD) {
      return queryRequest?.actionsFilter?.map(action => action.toString());
    }
    return queryRequest?.filters?.find(f => f.fieldName === filterState.name && f.isGlobal)?.values;
  }, [queryRequest?.filters, queryRequest?.actionsFilter, filterState.name]);

  const handleChange = (
    filterOperator: FilterOperator,
    newValue: HandleChangeValueUnionType | undefined | SelectionRange
  ) => {
    if (queryRequest && setQueryRequest && setCompositeFilterDescriptor) {
      const otherFieldFilters = queryRequest.filters?.filter(filter => filter.fieldName !== filterState.name);
      if (compositeFilterDescriptor) {
        const newCompositeFilterDescriptor: CompositeFilterDescriptor = {
          ...compositeFilterDescriptor,
          filters: (compositeFilterDescriptor.filters as CompositeFilterDescriptor[]).filter(filter1 =>
            (filter1.filters as FilterDescriptor[]).some(filter2 => filter2.field !== filterState.name)
          )
        };
        setCompositeFilterDescriptor(newCompositeFilterDescriptor);
      }

      const newFilters = otherFieldFilters || [];
      if (newValue && (Array.isArray(newValue) ? newValue.length > 0 : true)) {
        const newFilter: GearFilterRequest = {
          fieldName: filterState.name,
          operator: filterOperator,
          values: newValue,
          fieldType: filterState.type,
          isGlobal: true
        } as GearFilterRequest;
        newFilters.push(newFilter);
      }

      setQueryRequest({
        ...queryRequest,
        filters: newFilters
      });
      if (setSelectedDocuments) {
        setSelectedDocuments([]);
      }
    }
  };

  if (filterState.facetValues) {
    return (
      <MultiSelect
        label={filterState.displayName}
        data={filterState.facetValues.map(facetValue => facetValue.value)}
        value={currentValue || []}
        onChange={e => handleChange(FilterOperator.EqualTo, e.target.value)}
        id={filterState.id?.toString()}
      />
    );
  } else {
    switch (filterState.type) {
      case GEAR_FIELD_TYPE.BOOLEAN: {
        const booleanOptions = [
          {
            displayName: translate('custom.common.yes'),
            value: true.toString()
          },
          {
            displayName: translate('custom.common.no'),
            value: false.toString()
          }
        ];

        const selectedBooleanOptions =
          currentValue
            ?.map(stringValue => booleanOptions.find(option => option.value === stringValue))
            .filter(Boolean) || [];

        return (
          <MultiSelect
            label={filterState.displayName}
            data={booleanOptions}
            value={selectedBooleanOptions}
            onChange={e => {
              const values = e.target.value.map((item: any) => item.value);
              handleChange(FilterOperator.EqualTo, values);
            }}
            textField="displayName"
            dataItemKey="value"
            id={filterState.id?.toString()}
          />
        );
      }
      case GEAR_FIELD_TYPE.MULTICHOICE:
        if (filterState.name === PERMISSIONS_FIELD) {
          const actionOptions = Object.entries(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT)
            .filter(([key]) => key !== 'NONE')
            .map(([key, value]) => ({
              displayName: translate(`grid.filter${key.charAt(0) + key.slice(1).toLowerCase()}`),
              value: value.toString()
            }));

          const selectedActionOptions =
            currentValue
              ?.map(stringValue => actionOptions.find(option => option.value === stringValue))
              .filter(Boolean) || [];

          return (
            <MultiSelect
              label={filterState.name}
              data={actionOptions}
              value={selectedActionOptions}
              onChange={e => {
                const values = e.target.value.map((item: any) => parseInt(item.value, 10));

                if (queryRequest && setQueryRequest) {
                  setQueryRequest({
                    ...queryRequest,
                    actionsFilter: values.length > 0 ? values : undefined
                  });
                }
              }}
              textField="displayName"
              dataItemKey="value"
              id={filterState.id?.toString()}
            />
          );
        }

        return (
          <ControlledInput
            id={filterState.id.toString()}
            label={filterState.displayName}
            onChange={value => handleChange(FilterOperator.Contains, getArrayValue(value))}
            inputType="text"
            value={currentValue && currentValue.length === 1 ? (currentValue[0] as string) : undefined}
          />
        );
      case GEAR_FIELD_TYPE.INT32:
      case GEAR_FIELD_TYPE.INT64:
      case GEAR_FIELD_TYPE.NUMERIC:
        return (
          <ControlledInput
            id={filterState.id.toString()}
            label={filterState.displayName}
            onChange={value => handleChange(FilterOperator.EqualTo, getArrayValue(value))}
            inputType="number"
            value={currentValue && currentValue.length === 1 ? (currentValue[0] as string) : undefined}
          />
        );
      case GEAR_FIELD_TYPE.TEXT:
        return (
          <ControlledInput
            id={filterState.id.toString()}
            label={filterState.displayName}
            onChange={value => handleChange(FilterOperator.Contains, getArrayValue(value))}
            inputType="text"
            value={currentValue && currentValue.length === 1 ? (currentValue[0] as string) : undefined}
          />
        );
      default:
        return <></>;
    }
  }
};
